/**
 * Task Availability Utilities
 * 
 * Shared utilities for determining task states (available, active, completed).
 * Used across all views (Flow, Kanban, Table, Gantt, TodoList) for consistent
 * "actionable only" filtering.
 * 
 * A task is "actionable" if it's either:
 * - AVAILABLE: Ready to start (unlocked, not started, etc.)
 * - ACTIVE: Currently in progress
 */

import { DEFAULT_STATUSES } from '../../FlowchartView/TaskNode/constants';
import { calculateLockStates } from '../../FlowchartView/utils/dependencyUtils';
import { calculateProgress, isStatusCompleted, isStatusInProgress, isStatusNotStarted, isStatusBlocked } from '../../FlowchartView/utils/statusUtils';

/** Saved on the campaign and returned by the list, so a parent can inherit status without loading missions. */
export const CAMPAIGN_ACTIVITIES = ['not_started', 'in_progress', 'blocked', 'completed'];

export function readCampaignActivity(sprint) {
  const activity = sprint?.activity;
  return CAMPAIGN_ACTIVITIES.includes(activity) ? activity : null;
}

/**
 * Helper to get nested sprint for a task
 */
function getNestedSprint(task, nestedSprints) {
  if (!task.nestedSprintId) return null;
  return nestedSprints.find(ns => ns.id === task.nestedSprintId) || null;
}

function resolveChildNestedSprint(task, nestedSprints, allSprints) {
  if (!task.nestedSprintId) return null;
  return nestedSprints.find(ns => ns.id === task.nestedSprintId)
    || allSprints.find(ns => ns.id === task.nestedSprintId)
    || null;
}

function isNestedCampaignStarted(nestedSprint, statuses) {
  if (!nestedSprint) return false;

  // List rows have no missions. The saved label is the only honest signal.
  if (!Array.isArray(nestedSprint.tasks)) {
    const activity = readCampaignActivity(nestedSprint);
    if (activity === 'blocked' || activity === 'in_progress') return true;
    if (activity === 'completed' || activity === 'not_started') return false;
    if (nestedSprint.progress === 100) return false;
    if ((nestedSprint.progress || 0) > 0) return true;
    return false;
  }

  if (nestedSprint.progress === 100) return false;
  if ((nestedSprint.progress || 0) > 0) return true;
  const nestedStatuses = nestedSprint.statuses || statuses;
  return nestedSprint.tasks.some(t => isStatusInProgress(t.status, nestedStatuses));
}

function findNestedCampaign(task, nestedSprints, allSprints) {
  if (!task?.nestedSprintId) return null;
  return nestedSprints.find(ns => ns.id === task.nestedSprintId)
    || allSprints.find(ns => ns.id === task.nestedSprintId)
    || null;
}

function nestedCampaignIsKnown(sprint) {
  if (!sprint) return false;
  if (Array.isArray(sprint.tasks)) return true;
  return readCampaignActivity(sprint) != null;
}

/**
 * Actionable check for missions evaluated inside a nested campaign.
 * Does not treat "progress > 0" alone as in-progress — requires real workability.
 */
function isDirectTaskActionable(task, lockStates, nestedSprints, statuses, allSprints) {
  if (task.nodeType === 'decision' && task.answer != null) return false;
  if (isStatusCompleted(task.status, statuses)) return false;

  if (task.nestedSprintId) {
    const childSprint = resolveChildNestedSprint(task, nestedSprints, allSprints);
    if (!childSprint) return false;
    if (childSprint.progress === 100) return false;
    if (!Array.isArray(childSprint.tasks) && readCampaignActivity(childSprint) === 'completed') return false;
    if (!isNestedCampaignStarted(childSprint, statuses)) {
      return !lockStates[task.id]?.isLocked && isStatusNotStarted(task.status, statuses);
    }
    const actionable = hasNestedSprintActionableTask(
      { nestedSprintId: task.nestedSprintId },
      [childSprint, ...nestedSprints.filter(ns => ns.id !== childSprint.id)],
      statuses,
      allSprints,
    );
    return actionable === true;
  }

  if (lockStates[task.id]?.isLocked) return false;
  if (isStatusInProgress(task.status, statuses)) return true;
  return isStatusNotStarted(task.status, statuses) && !(task.nodeType === 'decision' && task.answer);
}

/**
 * Check if a task with nested sprint is effectively completed (100% progress)
 */
export function isNestedSprintCompleted(task, nestedSprints) {
  const nestedSprint = getNestedSprint(task, nestedSprints);
  if (!nestedSprint) return false;
  if (nestedSprint.progress === 100) return true;
  if (!Array.isArray(nestedSprint.tasks) && readCampaignActivity(nestedSprint) === 'completed') return true;
  return false;
}

/**
 * Nested campaign has begun but is not finished.
 */
export function isNestedSprintStarted(task, nestedSprints, statuses) {
  if (isNestedSprintCompleted(task, nestedSprints)) return false;
  const nestedSprint = getNestedSprint(task, nestedSprints);
  if (!nestedSprint) return false;
  return isNestedCampaignStarted(nestedSprint, statuses);
}

/**
 * Whether any mission inside the nested campaign can be worked on right now.
 * @returns {boolean|null} null when missions are not loaded and the campaign has no saved activity
 */
export function hasNestedSprintActionableTask(task, nestedSprints, statuses = DEFAULT_STATUSES, allSprints = []) {
  const nestedSprint = getNestedSprint(task, nestedSprints);
  if (!nestedSprint) return null;
  if (!Array.isArray(nestedSprint.tasks)) {
    const activity = readCampaignActivity(nestedSprint);
    if (activity === 'in_progress' || activity === 'not_started') return true;
    if (activity === 'blocked' || activity === 'completed') return false;
    return null;
  }
  const nestedTasks = nestedSprint.tasks;
  const nestedStatuses = nestedSprint.statuses || statuses;
  const grandchildSprints = allSprints.filter(s => s.parentSprintId === nestedSprint.id);
  const lockStates = calculateLockStates(nestedTasks, grandchildSprints, nestedStatuses);
  return nestedTasks.some(t =>
    isDirectTaskActionable(t, lockStates, grandchildSprints, nestedStatuses, allSprints)
  );
}

/**
 * Nested campaign is actively moving — at least one actionable inner mission.
 */
export function isNestedSprintInProgress(task, nestedSprints, statuses, allSprints = []) {
  if (!isNestedSprintStarted(task, nestedSprints, statuses)) return false;
  const actionable = hasNestedSprintActionableTask(task, nestedSprints, statuses, allSprints);
  if (actionable === null) return true;
  return actionable;
}

/**
 * Nested campaign has started but nothing inside is actionable right now.
 */
export function isNestedSprintBlocked(task, nestedSprints, statuses, allSprints = []) {
  if (!isNestedSprintStarted(task, nestedSprints, statuses)) return false;
  const actionable = hasNestedSprintActionableTask(task, nestedSprints, statuses, allSprints);
  if (actionable === null) return false;
  return !actionable;
}

/**
 * Check if a task is complete (either by status or nested sprint completion)
 */
export function isTaskComplete(task, nestedSprints, statuses) {
  // Decision nodes are complete if they have an answer
  if (task.nodeType === 'decision') {
    return task.answer != null;
  }
  
  return isNestedSprintCompleted(task, nestedSprints) || isStatusCompleted(task.status, statuses);
}

/**
 * Check if a task is active (in progress)
 */
export function isTaskActive(task, nestedSprints, statuses, allSprints = []) {
  if (isTaskComplete(task, nestedSprints, statuses)) return false;
  if (task.nestedSprintId) {
    return isNestedSprintInProgress(task, nestedSprints, statuses, allSprints);
  }
  return isStatusInProgress(task.status, statuses);
}

/**
 * Check if a task is blocked
 */
export function isTaskBlocked(task, nestedSprints, statuses, allSprints = []) {
  if (isTaskComplete(task, nestedSprints, statuses)) return false;
  if (task.nestedSprintId && isNestedSprintBlocked(task, nestedSprints, statuses, allSprints)) {
    return true;
  }
  return isStatusBlocked(task.status, statuses);
}

/**
 * Check if a task is available (ready to start)
 */
export function isTaskAvailable(task, lockStates, nestedSprints, statuses, allSprints = []) {
  if (isNestedSprintCompleted(task, nestedSprints)) return false;
  if (isNestedSprintInProgress(task, nestedSprints, statuses, allSprints)) return false;
  if (isNestedSprintBlocked(task, nestedSprints, statuses, allSprints)) return false;

  const isLocked = lockStates[task.id]?.isLocked;
  if (isLocked) return false;
  if (!isStatusNotStarted(task.status, statuses)) return false;
  if (task.nodeType === 'decision' && task.answer) return false;

  return true;
}

/**
 * Label for the campaign currently open, from its own missions.
 * Null when a nested campaign inside it is still an unlabeled shell — do not guess.
 */
export function deriveCampaignActivity(tasks, statuses = DEFAULT_STATUSES, nestedSprints = [], allSprints = []) {
  if (!Array.isArray(tasks)) return null;

  const hasUnknownNest = tasks.some(task => (
    task?.nestedSprintId && !nestedCampaignIsKnown(findNestedCampaign(task, nestedSprints, allSprints))
  ));
  if (hasUnknownNest) return null;

  const progress = calculateProgress(tasks, statuses, nestedSprints, allSprints);
  if (progress === 100) return 'completed';

  const hasInProgressTask = tasks.some(t => isStatusInProgress(t.status, statuses));
  if (progress === 0 && !hasInProgressTask) return 'not_started';

  const lockStates = calculateLockStates(tasks, nestedSprints, statuses);
  const anyActionable = tasks.some(t =>
    isDirectTaskActionable(t, lockStates, nestedSprints, statuses, allSprints)
  );
  return anyActionable ? 'in_progress' : 'blocked';
}

/**
 * Status the container mission should inherit from a nested campaign.
 * Null means the nested campaign is an unlabeled shell — leave the mission as it is.
 */
export function categoryForNestedCampaign(task, nestedSprints, statuses = DEFAULT_STATUSES, allSprints = []) {
  const nestedSprint = getNestedSprint(task, nestedSprints);
  if (!nestedSprint) return null;

  if (!Array.isArray(nestedSprint.tasks)) {
    if ((nestedSprint.progress || 0) === 100) return 'completed';
    return readCampaignActivity(nestedSprint);
  }

  const nestedStatuses = nestedSprint.statuses || statuses;
  const grandchildSprints = allSprints.filter(s => s.parentSprintId === nestedSprint.id);
  return deriveCampaignActivity(nestedSprint.tasks, nestedStatuses, grandchildSprints, allSprints);
}

/**
 * Check if a task is actionable (available OR active)
 */
export function isTaskActionable(task, lockStates, nestedSprints, statuses, allSprints = []) {
  return isTaskAvailable(task, lockStates, nestedSprints, statuses, allSprints)
    || isTaskActive(task, nestedSprints, statuses, allSprints);
}

/**
 * Calculate task states for a list of tasks
 */
export function calculateTaskStates(tasks, lockStates, nestedSprints, statuses, allSprints = []) {
  const states = {};
  
  tasks.forEach(task => {
    const isComplete = isTaskComplete(task, nestedSprints, statuses);
    const isActive = isTaskActive(task, nestedSprints, statuses, allSprints);
    const isBlocked = isTaskBlocked(task, nestedSprints, statuses, allSprints);
    const isAvailable = isTaskAvailable(task, lockStates, nestedSprints, statuses, allSprints);
    const isActionable = isAvailable || isActive;
    
    states[task.id] = { isComplete, isActive, isAvailable, isBlocked, isActionable };
  });
  
  return states;
}

/**
 * Filter tasks to only actionable ones (available OR active)
 */
export function filterActionableTasks(tasks, lockStates, nestedSprints, statuses, allSprints = []) {
  return tasks.filter(task => isTaskActionable(task, lockStates, nestedSprints, statuses, allSprints));
}
